import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCSDStatus,
  registrarCSD,
  diagnosticarCSD,
  getFacturas,
  facturarTicket,
  cancelarFactura,
  descargarFacturaPDF,
  descargarFacturaXML,
  enviarFacturaEmail,
  getRegimenesFiscales,
  getUsosCFDI,
  getFormasPago,
  getBusiness,
  getTicketsPendientesFacturar,
  getClients
} from '../../services/api';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Download, 
  Mail, 
  AlertCircle,
  Loader2,
  RefreshCw,
  Eye,
  Ban,
  Receipt,
  ArrowRight,
  Search,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const Facturacion = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tickets'); // tickets | facturas
  const [csdStatus, setCsdStatus] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [showFacturarModal, setShowFacturarModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [registrandoCSD, setRegistrandoCSD] = useState(false);
  const [business, setBusiness] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const [downloadingXML, setDownloadingXML] = useState(null);
  
  // Catalogs
  const [regimenes, setRegimenes] = useState([]);
  const [usosCFDI, setUsosCFDI] = useState([]);
  const [formasPago, setFormasPago] = useState([]);

  useEffect(() => {
    loadData();
  }, [user]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchTickets(searchQuery);
      } else {
        loadTickets();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const searchTickets = async (query) => {
    setSearching(true);
    try {
      const response = await getTicketsPendientesFacturar({ q: query, limit: 100 });
      setTickets(response.data);
    } catch (error) {
      console.error('Error searching tickets:', error);
    } finally {
      setSearching(false);
    }
  };

  const loadTickets = async () => {
    try {
      const response = await getTicketsPendientesFacturar({ limit: 100 });
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [csdRes, facturasRes, ticketsRes, businessRes] = await Promise.all([
        getCSDStatus(),
        getFacturas({ limit: 50 }),
        getTicketsPendientesFacturar({ limit: 100 }),
        getBusiness(user.business_id)
      ]);
      setCsdStatus(csdRes.data);
      setFacturas(facturasRes.data);
      setTickets(ticketsRes.data);
      setBusiness(businessRes.data);
      
      // Load catalogs
      const [regRes, usosRes, formasRes] = await Promise.all([
        getRegimenesFiscales(),
        getUsosCFDI(),
        getFormasPago()
      ]);
      setRegimenes(regRes.data);
      setUsosCFDI(usosRes.data);
      setFormasPago(formasRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos de facturación');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarCSD = async () => {
    setRegistrandoCSD(true);
    try {
      // First run diagnostics
      const diagResponse = await diagnosticarCSD();
      const diag = diagResponse.data;

      if (!diag.ok) {
        // Show all issues as a formatted message
        const issueText = diag.issues.map(i => `• ${i}`).join('\n');
        toast.error(`No se puede registrar el CSD:\n${issueText}\n\nVe a Configuración para corregir estos problemas.`, {
          duration: 15000
        });
        return;
      }

      // If diagnostics pass, proceed with registration
      const response = await registrarCSD();
      toast.success('Certificado digital registrado exitosamente');
      setCsdStatus({ registered: true, ...response.data });
    } catch (error) {
      console.error('Error registering CSD:', error);
      toast.error(getErrorMessage(error, 'Error al registrar certificado'), { duration: 15000 });
    } finally {
      setRegistrandoCSD(false);
    }
  };

  const handleDescargarPDF = async (factura) => {
    setDownloadingPDF(factura.id);
    try {
      const response = await descargarFacturaPDF(factura.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${factura.serie || ''}${factura.folio || ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error descargando PDF:', error);
      toast.error(getErrorMessage(error, 'Error al descargar PDF'));
    } finally {
      setDownloadingPDF(null);
    }
  };

  const handleDescargarXML = async (factura) => {
    setDownloadingXML(factura.id);
    try {
      const response = await descargarFacturaXML(factura.id);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${factura.serie || ''}${factura.folio || ''}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('XML descargado');
    } catch (error) {
      console.error('Error descargando XML:', error);
      toast.error(getErrorMessage(error, 'Error al descargar XML'));
    } finally {
      setDownloadingXML(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const paymentMethodLabels = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    telcash: 'Depósito'
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="facturacion-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Facturación Electrónica</h1>
            <p className="text-slate-500">CFDI 4.0 - Genera facturas desde tus tickets de venta</p>
          </div>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>

        {/* CSD Status Card */}
        <div className={`rounded-xl border p-6 ${csdStatus?.registered ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${csdStatus?.registered ? 'bg-green-100' : 'bg-amber-100'}`}>
              {csdStatus?.registered ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-amber-600" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900">
                {csdStatus?.registered ? 'Certificado Digital Activo' : 'Certificado Digital No Registrado'}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {csdStatus?.registered 
                  ? `RFC: ${csdStatus.rfc} - Listo para emitir facturas`
                  : 'Registra tu certificado digital (CSD) para comenzar a facturar'
                }
              </p>
              {!csdStatus?.registered && business?.settings?.billing_enabled && (
                <button
                  onClick={handleRegistrarCSD}
                  disabled={registrandoCSD}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                  data-testid="registrar-csd-btn"
                >
                  {registrandoCSD ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Registrar Certificado Digital
                    </>
                  )}
                </button>
              )}
              {!business?.settings?.billing_enabled && (
                <p className="mt-2 text-sm text-amber-700">
                  Primero habilita la facturación en Configuración y sube tu certificado (.cer) y llave privada (.key)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Receipt className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{tickets.length}</p>
                <p className="text-sm text-slate-500">Tickets por Facturar</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{facturas.length}</p>
                <p className="text-sm text-slate-500">Total Facturas</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {facturas.filter(f => f.estatus === 'vigente').length}
                </p>
                <p className="text-sm text-slate-500">Vigentes</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <FileText className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(facturas.reduce((sum, f) => f.estatus === 'vigente' ? sum + f.total : sum, 0))}
                </p>
                <p className="text-sm text-slate-500">Total Facturado</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('tickets')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tickets'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Tickets Pendientes ({tickets.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('facturas')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'facturas'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Facturas Emitidas ({facturas.length})
              </div>
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {activeTab === 'tickets' ? (
            <>
              {/* Search Bar */}
              <div className="p-4 border-b border-slate-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por número de ticket o ID..."
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="search-ticket-input"
                  />
                  {searching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-600 animate-spin" />
                  )}
                </div>
              </div>
              
              {/* Tickets Table */}
              {tickets.length === 0 ? (
                <div className="p-8 text-center">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">
                    {searchQuery ? 'No se encontraron tickets con ese criterio' : 'No hay tickets pendientes de facturar'}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {searchQuery ? 'Intenta con otro número de ticket' : 'Los tickets de venta aparecerán aquí cuando se registren desde el POS'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ticket</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sucursal</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Método Pago</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Productos</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {tickets.map((ticket) => (
                        <tr key={ticket.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900 font-mono">
                            {ticket.ticket_number}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {formatDate(ticket.created_at)}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {ticket.branch_name || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              {paymentMethodLabels[ticket.payment_method] || ticket.payment_method}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-slate-600">
                            {ticket.items?.length || 0}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-slate-900">
                            {formatCurrency(ticket.total)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setShowFacturarModal(true);
                              }}
                              disabled={!csdStatus?.registered}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid={`facturar-ticket-${ticket.id}`}
                            >
                              Facturar
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Facturas Table */}
              {facturas.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay facturas emitidas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Serie/Folio</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Receptor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">RFC</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Estatus</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {facturas.map((factura) => (
                        <tr key={factura.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {factura.serie}{factura.folio}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {formatDate(factura.fecha || factura.created_at)}
                          </td>
                          <td className="px-4 py-3 text-slate-900">
                            {factura.receptor_nombre}
                          </td>
                          <td className="px-4 py-3 text-slate-600 font-mono text-sm">
                            {factura.receptor_rfc}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-slate-900">
                            {formatCurrency(factura.total)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              factura.estatus === 'vigente' 
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {factura.estatus === 'vigente' ? 'Vigente' : 'Cancelada'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handleDescargarPDF(factura)}
                                disabled={downloadingPDF === factura.id}
                                className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Descargar PDF"
                              >
                                {downloadingPDF === factura.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDescargarXML(factura)}
                                disabled={downloadingXML === factura.id}
                                className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Descargar XML"
                              >
                                {downloadingXML === factura.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                ) : (
                                  <FileText className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedFactura(factura);
                                  setShowDetalleModal(true);
                                }}
                                className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Ver Detalles"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Facturar Ticket */}
        {showFacturarModal && selectedTicket && (
          <FacturarTicketModal
            ticket={selectedTicket}
            onClose={() => {
              setShowFacturarModal(false);
              setSelectedTicket(null);
            }}
            onSuccess={() => {
              setShowFacturarModal(false);
              setSelectedTicket(null);
              loadData();
            }}
            regimenes={regimenes}
            usosCFDI={usosCFDI}
            formasPago={formasPago}
          />
        )}

        {/* Modal Detalle Factura */}
        {showDetalleModal && selectedFactura && (
          <DetalleFacturaModal
            factura={selectedFactura}
            onClose={() => {
              setShowDetalleModal(false);
              setSelectedFactura(null);
            }}
            onDescargarPDF={() => handleDescargarPDF(selectedFactura)}
            onDescargarXML={() => handleDescargarXML(selectedFactura)}
            downloadingPDF={downloadingPDF === selectedFactura?.id}
            downloadingXML={downloadingXML === selectedFactura?.id}
            onCancelar={async (motivo) => {
              try {
                await cancelarFactura(selectedFactura.id, { motivo });
                toast.success('Factura cancelada exitosamente');
                setShowDetalleModal(false);
                loadData();
              } catch (error) {
                toast.error(getErrorMessage(error, 'Error al cancelar factura'));
              }
            }}
            onEnviarEmail={async (email) => {
              try {
                await enviarFacturaEmail(selectedFactura.id, { email });
                toast.success(`Factura enviada a ${email}`);
              } catch (error) {
                toast.error('Error al enviar factura por email');
              }
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

// Modal para facturar desde ticket
const FacturarTicketModal = ({ ticket, onClose, onSuccess, regimenes, usosCFDI, formasPago }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    rfc: '',
    nombre: '',
    regimen_fiscal: '601',
    uso_cfdi: 'G03',
    domicilio_fiscal: '',
    enviar_email: ''
  });
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2 || !user?.business_id) {
      setClientResults([]);
      setShowClientDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await getClients(user.business_id, { q: clientSearch });
        setClientResults(res.data.slice(0, 10));
        setShowClientDropdown(res.data.length > 0);
      } catch (e) {
        console.error('Error searching clients:', e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch, user?.business_id]);

  const handleSelectClient = (client) => {
    setFormData({
      rfc: client.rfc,
      nombre: client.razon_social,
      regimen_fiscal: client.regimen_fiscal || '601',
      uso_cfdi: client.uso_cfdi || 'G03',
      domicilio_fiscal: client.domicilio_fiscal || '',
      enviar_email: client.email || ''
    });
    setClientSearch('');
    setClientResults([]);
    setShowClientDropdown(false);
  };

  const paymentMethodMap = {
    cash: '01',
    card: '04',
    transfer: '03',
    telcash: '01'
  };

  const paymentMethodLabels = {
    cash: 'Efectivo',
    card: 'Tarjeta de Crédito/Débito',
    transfer: 'Transferencia Electrónica',
    telcash: 'Depósito en Efectivo'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await facturarTicket({
        ticket_id: ticket.id,
        receptor: {
          rfc: formData.rfc.toUpperCase(),
          nombre: formData.nombre,
          regimen_fiscal: formData.regimen_fiscal,
          uso_cfdi: formData.uso_cfdi,
          domicilio_fiscal: formData.domicilio_fiscal
        },
        enviar_email: formData.enviar_email || null
      });
      toast.success('Factura generada exitosamente');
      onSuccess();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(getErrorMessage(error, 'Error al generar factura'));
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-slate-900">Facturar Ticket</h2>
          <p className="text-sm text-slate-500 mt-1">Ticket: {ticket.ticket_number}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Detalle del Ticket */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="font-semibold text-slate-900 mb-3">Detalle del Ticket</h3>
            <div className="space-y-2">
              {ticket.items?.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-slate-600">
                    {item.quantity}x {item.product_name}
                  </span>
                  <span className="text-slate-900">{formatCurrency(item.subtotal)}</span>
                </div>
              ))}
              <div className="border-t border-slate-200 pt-2 mt-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span>{formatCurrency(ticket.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">IVA:</span>
                  <span>{formatCurrency(ticket.tax)}</span>
                </div>
                <div className="flex justify-between font-bold mt-1">
                  <span>Total:</span>
                  <span className="text-teal-600">{formatCurrency(ticket.total)}</span>
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Método de pago:</span> {paymentMethodLabels[ticket.payment_method] || ticket.payment_method}
                <span className="text-slate-400 ml-2">(Código SAT: {paymentMethodMap[ticket.payment_method] || '01'})</span>
              </p>
            </div>
          </div>

          {/* Selector de Cliente Guardado */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-teal-800 mb-2">
              <UserCheck className="w-4 h-4 inline mr-1" />
              Seleccionar cliente guardado (opcional)
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-400" />
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Buscar por RFC o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 bg-white"
              />
              {showClientDropdown && clientResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {clientResults.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleSelectClient(client)}
                      className="w-full text-left px-4 py-2 hover:bg-teal-50 transition-colors border-b border-slate-100 last:border-0"
                    >
                      <span className="font-mono text-sm text-teal-700">{client.rfc}</span>
                      <span className="text-sm text-slate-600 ml-2">- {client.razon_social}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Datos del Cliente */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900">Datos del Cliente (Receptor)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">RFC *</label>
                <input
                  type="text"
                  value={formData.rfc}
                  onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono uppercase"
                  placeholder="XAXX010101000"
                  maxLength={13}
                  required
                  data-testid="receptor-rfc-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre o Razón Social *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 uppercase"
                  required
                  data-testid="receptor-nombre-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Régimen Fiscal *</label>
                <select
                  value={formData.regimen_fiscal}
                  onChange={(e) => setFormData({ ...formData, regimen_fiscal: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  required
                >
                  {regimenes.map(r => (
                    <option key={r.Value} value={r.Value}>{r.Value} - {r.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Uso CFDI *</label>
                <select
                  value={formData.uso_cfdi}
                  onChange={(e) => setFormData({ ...formData, uso_cfdi: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  required
                >
                  {usosCFDI.map(u => (
                    <option key={u.Value} value={u.Value}>{u.Value} - {u.Name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Código Postal Fiscal *</label>
                <input
                  type="text"
                  value={formData.domicilio_fiscal}
                  onChange={(e) => setFormData({ ...formData, domicilio_fiscal: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="00000"
                  maxLength={5}
                  pattern="[0-9]{5}"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email (opcional)</label>
                <input
                  type="email"
                  value={formData.enviar_email}
                  onChange={(e) => setFormData({ ...formData, enviar_email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="cliente@ejemplo.com"
                />
                <p className="text-xs text-slate-500 mt-1">Se enviará la factura a este correo</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
              data-testid="emitir-factura-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Generar Factura
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para ver detalles de factura
const DetalleFacturaModal = ({ factura, onClose, onCancelar, onEnviarEmail, onDescargarPDF, onDescargarXML, downloadingPDF, downloadingXML }) => {
  const [showCancelar, setShowCancelar] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('02');
  const [emailEnvio, setEmailEnvio] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviarEmail = async () => {
    if (!emailEnvio) return;
    setEnviando(true);
    await onEnviarEmail(emailEnvio);
    setEnviando(false);
    setEmailEnvio('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-900">
            Factura {factura.serie}{factura.folio}
          </h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            factura.estatus === 'vigente' 
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {factura.estatus === 'vigente' ? 'Vigente' : 'Cancelada'}
          </span>
        </div>
        
        <div className="p-6 space-y-6">
          {/* UUID */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">UUID (Folio Fiscal)</p>
            <p className="font-mono text-sm text-slate-900 break-all">{factura.uuid || 'N/A'}</p>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 mb-1">Emisor</p>
              <p className="font-medium text-slate-900">{factura.emisor_nombre}</p>
              <p className="text-sm text-slate-600 font-mono">{factura.emisor_rfc}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Receptor</p>
              <p className="font-medium text-slate-900">{factura.receptor_nombre}</p>
              <p className="text-sm text-slate-600 font-mono">{factura.receptor_rfc}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Fecha</p>
              <p className="text-slate-900">{new Date(factura.fecha || factura.created_at).toLocaleString('es-MX')}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Método de Pago</p>
              <p className="text-slate-900">{factura.metodo_pago}</p>
            </div>
          </div>

          {/* Totales */}
          <div className="bg-teal-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-teal-700">Subtotal:</span>
              <span className="text-teal-900">${factura.subtotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-teal-700">IVA:</span>
              <span className="text-teal-900">${factura.iva?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-teal-200">
              <span className="text-teal-900">Total:</span>
              <span className="text-teal-900">${factura.total?.toFixed(2)}</span>
            </div>
          </div>

          {/* Download buttons */}
          <div className="flex gap-3">
            <button
              onClick={onDescargarPDF}
              disabled={downloadingPDF}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {downloadingPDF ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {downloadingPDF ? 'Generando PDF...' : 'Descargar PDF'}
            </button>
            <button
              onClick={onDescargarXML}
              disabled={downloadingXML}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              {downloadingXML ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              {downloadingXML ? 'Descargando...' : 'Descargar XML'}
            </button>
          </div>

          {/* Enviar por email */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Enviar por email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={emailEnvio}
                onChange={(e) => setEmailEnvio(e.target.value)}
                placeholder="correo@ejemplo.com"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={handleEnviarEmail}
                disabled={!emailEnvio || enviando}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Enviar
              </button>
            </div>
          </div>

          {/* Cancelación */}
          {factura.estatus === 'vigente' && (
            <div className="border-t border-slate-200 pt-4">
              {!showCancelar ? (
                <button
                  onClick={() => setShowCancelar(true)}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  <Ban className="w-4 h-4" />
                  Cancelar Factura
                </button>
              ) : (
                <div className="bg-red-50 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-red-800 font-medium">¿Está seguro de cancelar esta factura?</p>
                  <div>
                    <label className="block text-sm text-red-700 mb-1">Motivo de cancelación</label>
                    <select
                      value={motivoCancelacion}
                      onChange={(e) => setMotivoCancelacion(e.target.value)}
                      className="w-full px-3 py-2 border border-red-200 rounded-lg focus:ring-2 focus:ring-red-500"
                    >
                      <option value="01">01 - Comprobante emitido con errores con relación</option>
                      <option value="02">02 - Comprobante emitido con errores sin relación</option>
                      <option value="03">03 - No se llevó a cabo la operación</option>
                      <option value="04">04 - Operación nominativa relacionada con factura global</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCancelar(false)}
                      className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                    >
                      No, mantener
                    </button>
                    <button
                      onClick={() => onCancelar(motivoCancelacion)}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Sí, cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default Facturacion;
